/**
 * Markdown rendering for assistant answers.
 *
 * Pipeline: `marked` (GFM) → DOM post-processing (citation markers + code
 * highlighting + safe external links) → `DOMPurify` sanitization.
 *
 * `renderMarkdownWithCitations` replaces inline hyperlink references whose URL
 * matches a derived citation with a compact, risk-colored, keyboard-accessible
 * marker button (e.g. a colored dot + number). Numbering is supplied by the
 * caller (see `lib/citations.ts`) so markers and the reference list agree.
 */

import { marked } from 'marked'
import DOMPurify from 'dompurify'
import type { Config as DOMPurifyConfig } from 'dompurify'
import hljs from 'highlight.js'
import type { Citation } from '@/types'
import { canonicalizeUrl, citationMapByUrl, LINK_RE, normalizeMcpCitationLinks } from './citations'
import { getRiskLabel } from './riskTier'

marked.setOptions({ gfm: true, breaks: false })

const SANITIZE_CONFIG: DOMPurifyConfig = {
  ADD_TAGS: ['button'],
  ADD_ATTR: ['type', 'data-cite-id', 'data-cite-tier', 'data-cite-url', 'target', 'rel'],
}

// Copy output uses plain `<sup>[n]</sup>` inline markers (not links): browsers
// stamp the source page URL onto clipboard HTML, so a relative `#ref-n` href
// would resolve to "https://<app>/#ref-n" in most paste targets. The trailing
// References list carries the real, clickable source links instead.
const COPY_SANITIZE_CONFIG: DOMPurifyConfig = {
  ADD_TAGS: ['sup'],
  ADD_ATTR: ['id', 'href', 'target', 'rel'],
}

function parseToBody(content: string): HTMLElement {
  // Normalize legacy MCP `(#)` cites before markdown parse so markers and the
  // DOM href agree on the synthetic mcp:// target.
  const normalized = normalizeMcpCitationLinks(content)
  const rawHtml = marked.parse(normalized, { async: false }) as string
  const doc = new DOMParser().parseFromString(rawHtml, 'text/html')
  return doc.body
}

function highlightCode(body: HTMLElement): void {
  body.querySelectorAll('pre code').forEach((el) => {
    try {
      hljs.highlightElement(el as HTMLElement)
    } catch {
      /* highlighting is best-effort */
    }
  })
}

function hardenLinks(body: HTMLElement): void {
  body.querySelectorAll('a[href]').forEach((a) => {
    a.setAttribute('target', '_blank')
    a.setAttribute('rel', 'noopener noreferrer')
  })
}

function markerAriaLabel(c: Citation): string {
  const parts = [`Citation ${c.id}`]
  if (c.publisher) parts.push(c.publisher)
  // MCP aggregates have no event risk tier — don't announce "N/A risk".
  if (!(c.sourceType === 'mcp' || /^mcp:\/\//i.test(c.url))) {
    parts.push(getRiskLabel(c.riskTier))
  } else {
    parts.push('MCP call')
  }
  return parts.join(', ')
}

/**
 * Replace anchors that correspond to a derived citation with an inline marker
 * button. Non-citation links are left intact (and hardened in `hardenLinks`).
 */
function applyCitationMarkers(body: HTMLElement, citations: Citation[]): void {
  if (!citations.length) return
  const byUrl = citationMapByUrl(citations)
  const doc = body.ownerDocument

  Array.from(body.querySelectorAll('a[href]')).forEach((a) => {
    const href = a.getAttribute('href') || ''
    const c = byUrl.get(canonicalizeUrl(href))
    if (!c) return

    const marker = doc.createElement('button')
    marker.setAttribute('type', 'button')
    marker.className = 'citation-marker'
    marker.setAttribute('data-cite-id', String(c.id))
    marker.setAttribute('data-cite-tier', c.riskTier == null ? 'na' : String(c.riskTier))
    marker.setAttribute('data-cite-url', c.url)
    marker.setAttribute('aria-label', markerAriaLabel(c))

    const dot = doc.createElement('span')
    dot.className = 'citation-dot'
    dot.setAttribute('aria-hidden', 'true')

    const num = doc.createElement('span')
    num.className = 'citation-num'
    num.textContent = String(c.id)

    marker.appendChild(dot)
    marker.appendChild(num)
    a.replaceWith(marker)
  })
}

/** Render markdown to sanitized HTML (no citation transform). */
export function renderMarkdown(content: string): string {
  if (!content) return ''
  const body = parseToBody(content)
  highlightCode(body)
  hardenLinks(body)
  return DOMPurify.sanitize(body.innerHTML, SANITIZE_CONFIG) as string
}

/**
 * Render markdown to sanitized HTML, replacing citation hyperlinks with
 * risk-tiered inline markers. Pass the numbered citations derived from the same
 * content (see `parseCitationsFromMarkdown`).
 */
export function renderMarkdownWithCitations(content: string, citations: Citation[]): string {
  if (!content) return ''
  const body = parseToBody(content)
  highlightCode(body)
  applyCitationMarkers(body, citations)
  hardenLinks(body)
  return DOMPurify.sanitize(body.innerHTML, SANITIZE_CONFIG) as string
}

/** Replace each citation anchor with a plain bracketed `[n]` superscript. */
function applyCopyMarkers(body: HTMLElement, citations: Citation[]): void {
  if (!citations.length) return
  const byUrl = citationMapByUrl(citations)
  const doc = body.ownerDocument

  Array.from(body.querySelectorAll('a[href]')).forEach((a) => {
    const href = a.getAttribute('href') || ''
    const c = byUrl.get(canonicalizeUrl(href))
    if (!c) return

    const sup = doc.createElement('sup')
    sup.className = 'cite-ref'
    sup.textContent = `[${c.id}]`
    a.replaceWith(sup)
  })
}

/** Append a `References` section whose entries carry stable `#ref-n` ids. */
function appendReferences(body: HTMLElement, citations: Citation[]): void {
  const doc = body.ownerDocument
  const heading = doc.createElement('p')
  const strong = doc.createElement('strong')
  strong.textContent = 'References'
  heading.appendChild(strong)
  body.appendChild(heading)

  const list = doc.createElement('ol')
  for (const c of citations) {
    const li = doc.createElement('li')
    const linkable = /^https?:\/\//i.test((c.url || '').trim())
    if (linkable) {
      const link = doc.createElement('a')
      link.setAttribute('href', c.url)
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
      link.textContent = c.title
      li.appendChild(link)
    } else {
      li.appendChild(doc.createTextNode(c.title))
    }
    const suffix =
      c.sourceType === 'mcp' || /^mcp:\/\//i.test(c.url || '')
        ? 'MCP call'
        : getRiskLabel(c.riskTier)
    li.appendChild(doc.createTextNode(` \u2014 ${suffix}`))
    list.appendChild(li)
  }
  body.appendChild(list)
}

/** Plain-text variant: inline `[n]` markers plus a trailing References block. */
function buildCopyText(content: string, citations: Citation[]): string {
  const byUrl = citationMapByUrl(citations)
  LINK_RE.lastIndex = 0
  const answer = content.replace(LINK_RE, (match, bang, _label, rawUrl) => {
    if (bang === '!') return match
    let url = String(rawUrl).trim()
    if (url.startsWith('<') && url.endsWith('>')) url = url.slice(1, -1).trim()
    const c = byUrl.get(canonicalizeUrl(url))
    return c ? `[${c.id}]` : match
  })
  if (!citations.length) return answer
  const lines = ['', 'References']
  for (const c of citations) {
    const linkable = /^https?:\/\//i.test((c.url || '').trim())
    if (linkable) {
      lines.push(`[${c.id}] ${c.title} \u2014 ${c.url}`)
    } else if (c.sourceType === 'mcp' || /^mcp:\/\//i.test(c.url || '')) {
      lines.push(`[${c.id}] ${c.title} \u2014 MCP call`)
    } else {
      lines.push(`[${c.id}] ${c.title}`)
    }
  }
  return `${answer}\n${lines.join('\n')}`
}

/**
 * Static HTML for a print document: bracketed `[n]` inline markers and hardened
 * links, but with NO references appended (the print builder positions the
 * references after the graphs) and no interactive citation buttons.
 */
export function renderAnswerForPrint(content: string, citations: Citation[]): string {
  if (!content) return ''
  const body = parseToBody(content)
  hardenLinks(body)
  applyCopyMarkers(body, citations)
  return DOMPurify.sanitize(body.innerHTML, COPY_SANITIZE_CONFIG) as string
}

/**
 * Build paste-friendly clipboard payloads for an answer: bracketed `[n]` inline
 * markers and an appended numbered `References` list, as both rich HTML (with
 * best-effort in-document `#ref-n` links) and plain text.
 */
export function renderAnswerForCopy(
  content: string,
  citations: Citation[],
): { html: string; text: string } {
  if (!content) return { html: '', text: '' }
  const body = parseToBody(content)
  hardenLinks(body)
  applyCopyMarkers(body, citations)
  if (citations.length) appendReferences(body, citations)
  const html = DOMPurify.sanitize(body.innerHTML, COPY_SANITIZE_CONFIG) as string
  return { html, text: buildCopyText(content, citations) }
}
